import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Users, ChevronUp, ChevronDown } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { useLobbyStore, useSelectedCharacter } from '@/lib/stores/lobbyStore';
import { usePanelCollapse } from '@/lib/hooks/usePanelCollapse';
import { HpBar } from '@/components/ui/hp-bar';
import { LeaderCrown } from '@/components/ui/leader-crown';
import { StatusPill } from '@/components/ui/status-pill';
import { getCharacterStatus, resolveMemberIsDead, effectiveMaxHp } from '@/lib/character-status';
import { deriveHealthState, visibleConditions } from '@/lib/stores/gameStore';
import { ConditionBadge } from '@/components/ui/condition-badge';
import { ResurrectionPenaltyBadge } from '@/components/ui/resurrection-penalty-badge';
import ImageModal from '@/components/common/ImageModal';
import type { HealthState, WireConditionEntry } from '@danda/shared';

// Synthesize a 'dead' condition entry when permanent.status says dead but the
// session HP / conditions haven't caught up. Lobby chat already treats
// permanent.status as the source of truth for death (see LobbyRoomContent.tsx);
// the panel must agree, otherwise the PC tile reads 'alive' while chat correctly
// gates Aria out (the post-TPK bug). Returns the conditions array possibly
// augmented with a synthetic 'dead' entry.
//
// Module-scope so the closure is allocated once instead of on every render.
function ensureDeadCondition(
  permanentStatus: string | undefined,
  conditions: WireConditionEntry[]
): WireConditionEntry[] {
  if (permanentStatus !== 'dead') return conditions;
  const alreadyDead = conditions.some(
    (c) => (typeof c === 'string' ? c : c.name).toLowerCase() === 'dead'
  );
  return alreadyDead ? conditions : [...conditions, 'dead'];
}

/**
 * Party Panel - Shows party members in lobby (matches game session PartyStatusPanel style)
 *
 * Displays:
 * - Party name as title
 * - Party hero image (when available)
 * - Each member with:
 *   - Avatar, name, status badge
 *   - Level/species/class info
 *   - Player's char: HP bar with current/max values
 *   - Other chars: Health indicator (Uninjured/Bloodied/Critical/Dying/Dead) + conditions
 */

/** Health state badge styling for observable health (matches game session) */
const healthStateBadgeConfig: Record<HealthState, { label: string; className: string }> = {
  uninjured: { label: 'UNINJURED', className: 'bg-green-600 text-white' },
  wounded: { label: 'WOUNDED', className: 'bg-yellow-600 text-white' },
  bloodied: { label: 'BLOODIED', className: 'bg-orange-600 text-white' },
  critical: { label: 'CRITICAL', className: 'bg-red-600 text-white' },
  dying: { label: 'DYING', className: 'bg-red-900 text-white' },
  dead: { label: 'DEAD', className: 'bg-gray-800 text-gray-200' },
};
export default function PartyPanel() {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageOverlayOpen, setImageOverlayOpen] = useState(false);
  const [memberPortraitOverlay, setMemberPortraitOverlay] = useState<{
    url: string;
    name: string;
  } | null>(null);
  const [isExpanded, toggleExpanded] = usePanelCollapse('party');

  const { parties, chatRooms, lobbyCharacters, characters } = useLobbyStore(
    useShallow((s) => ({
      parties: s.parties,
      chatRooms: s.chatRooms,
      lobbyCharacters: s.lobbyCharacters,
      characters: s.characters,
    }))
  );
  const selectedCharacter = useSelectedCharacter();

  // Find the party that the selected character belongs to
  const currentParty = useMemo(() => {
    if (!selectedCharacter) return null;
    return parties.find((p) => p.memberCharacterIds.includes(selectedCharacter.id)) ?? null;
  }, [parties, selectedCharacter]);

  // Use partyImageUrl from party state (persisted to Firestore, available on reconnect).
  // Chat tags are checked as fallback for the brief window before party state updates.
  const partyHeroImage = useMemo(() => {
    if (!currentParty) return null;

    // Primary: party state (always available on reconnect via lobby:updated)
    if (currentParty.partyImageUrl) return currentParty.partyImageUrl;

    // Fallback: scan chat tags for recently generated images not yet in party state
    const partyRoomId = `party-${currentParty.id}`;
    const partyRoom = chatRooms.find((r) => r.id === partyRoomId);
    if (partyRoom) {
      for (let i = partyRoom.messages.length - 1; i >= 0; i--) {
        const msg = partyRoom.messages[i];
        const imageTag = /\[PARTY_IMAGE:([^\]]+)\]/.exec(msg.content);
        if (imageTag) {
          return imageTag[1];
        }
      }
    }

    return null;
  }, [currentParty, chatRooms]);

  // Reset image loading state when image URL changes
  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
  }, [partyHeroImage, currentParty?.id]);

  // Don't render if no character or not in a party
  if (!selectedCharacter || !currentParty) {
    return null;
  }

  // ============================================================
  // HP / status / conditions — Source of Truth (lobby panels)
  // ============================================================
  // Canonical (read these in `state.characters[i]`):
  //   • HP                  → session.hp
  //   • maxHP               → permanent.max_hp
  //   • status              → permanent.status (serde-lowercase: 'active' | 'dead')
  //   • conditions          → session.conditions
  //   • exhaustion          → session.exhaustion_level
  //   • resurrection penalty → session.resurrection_penalty
  //
  // Two mirror directions keep the denormalized `parties[].members[]` cache
  // in sync with the canonical Character object — see character-events.ts
  // (forward, per-character CharacterUpdated) and party-events.ts (inverse,
  // change-gated to avoid stale clobber). PartyMemberInfo now carries
  // currentHp/maxHp/conditions/exhaustionLevel/resurrectionPenalty, so a
  // PartyUpdated broadcast alone (e.g. after a long rest) is sufficient to
  // refresh all of these for the SELECTED character below, without waiting
  // on a separate CharacterUpdated.
  //
  // Read order here matches the SOT: try the Character store first, fall
  // back to the party-member cache only when the Character hasn't been
  // loaded yet (e.g., NPC just joined). This keeps the party panel and
  // CharDetailsPanel agreeing on every render.
  //
  // NOTE: for OTHER party members (below), `perm`/`sess` come from the
  // `characters` store, which this client only ever populates for its OWN
  // character(s) — `setCharacters` has a single production call site
  // (LobbyLayout.tsx, fed by `apiClient.getCharacters()`), and the
  // character-events handlers only map over rows that already exist. So `sess`
  // is ALWAYS undefined for a teammate, and the `member.*` fallbacks below are
  // what make their conditions and resurrection penalty render at all.
  // ============================================================
  const membersWithData = currentParty.members.map((member) => {
    // Check if this member is the player's selected character
    if (member.id === selectedCharacter.id) {
      const permanent = selectedCharacter.permanent;
      const session = selectedCharacter.session;
      // Fall back to the PartyUpdated cache: `characters` only ever holds the
      // signed-in user's own characters, so for anyone else `session` is
      // undefined and the wire copy is the only source.
      const baseConditions = (session?.conditions ??
        member.conditions ??
        []) as WireConditionEntry[];
      return {
        ...member,
        avatarUrl:
          selectedCharacter.avatarUrl ||
          selectedCharacter.avatar ||
          (permanent?.avatar_url as string | undefined) ||
          (permanent?.avatarUrl as string | undefined),
        avatarEmoji: member.avatarEmoji || selectedCharacter.avatarEmoji || permanent?.avatar_emoji,
        portraitUrl: member.portraitUrl ?? selectedCharacter.portraitUrl,
        level: permanent?.level ?? 1,
        species: permanent?.species,
        class: permanent?.class,
        hp: session?.hp ?? member.currentHp ?? permanent?.max_hp ?? 10,
        maxHp: permanent ? effectiveMaxHp(permanent, session) : (member.maxHp ?? 10),
        conditions: ensureDeadCondition(permanent?.status, baseConditions),
        isDead: resolveMemberIsDead(permanent, member.isDead),
        resurrectionPenalty: session?.resurrection_penalty ?? member.resurrectionPenalty,
        hasSessionData: true,
        isCurrentPlayer: true,
      };
    }

    // Look up other members from lobbyCharacters or characters store
    const lobbyChar = lobbyCharacters.find((c) => c.id === member.id);
    const storeChar = characters.find((c) => c.id === member.id);
    const charData = storeChar || lobbyChar;

    // storeChar (Character) has permanent/session; lobbyChar (LobbyCharacterInfo) has level/species/class directly
    // member (PartyMemberInfo) has currentHp/maxHp from the party state
    const perm = storeChar?.permanent;
    const sess = storeChar?.session;
    // See the note above: teammates never have a `characters` entry, so the
    // PartyUpdated cache is what makes their conditions render at all.
    const baseConditions = (sess?.conditions ?? member.conditions ?? []) as WireConditionEntry[];

    return {
      ...member,
      avatarUrl: member.avatarUrl || charData?.avatarUrl || charData?.avatar,
      avatarEmoji: member.avatarEmoji || charData?.avatarEmoji,
      level: perm?.level ?? member.level ?? 1,
      species: perm?.species ?? member.species,
      class: perm?.class ?? member.class,
      hp: sess?.hp ?? member.currentHp ?? perm?.max_hp ?? 10,
      maxHp: perm ? effectiveMaxHp(perm, sess) : (member.maxHp ?? 10),
      conditions: ensureDeadCondition(perm?.status, baseConditions),
      isDead: resolveMemberIsDead(perm, member.isDead),
      resurrectionPenalty: sess?.resurrection_penalty ?? member.resurrectionPenalty,
      isCurrentPlayer: false,
    };
  });

  return (
    <Card className="isolate">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Users className="h-4 w-4" />
            {currentParty.name}
          </CardTitle>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={toggleExpanded}
            aria-label={isExpanded ? 'Collapse panel' : 'Expand panel'}
          >
            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </div>
      </CardHeader>
      {isExpanded && (
        <CardContent className="p-3 pt-0 space-y-3 select-none">
          {/* Party Hero Image */}
          {partyHeroImage && !imageError && (
            <div className="overflow-hidden rounded-md">
              <img
                src={partyHeroImage}
                alt={`${currentParty.name} party image`}
                className={`w-full aspect-video object-cover rounded-md transition-opacity duration-300 cursor-pointer ${
                  imageLoaded ? 'opacity-100' : 'opacity-0'
                }`}
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
                onClick={() => imageLoaded && setImageOverlayOpen(true)}
              />
            </div>
          )}
          <ImageModal
            isOpen={imageOverlayOpen}
            onClose={() => setImageOverlayOpen(false)}
            imageUrl={partyHeroImage ?? undefined}
            altText={`${currentParty.name} party image`}
          />

          {/* Party Members - Game Session Style (player's char always on top) */}
          {[...membersWithData]
            .sort((a, b) => Number(b.isCurrentPlayer) - Number(a.isCurrentPlayer))
            .map((member) => {
              const status = getCharacterStatus(
                member.hp,
                member.maxHp,
                member.conditions,
                member.isDead
              );
              const healthState = deriveHealthState(
                member.hp,
                member.maxHp,
                member.conditions,
                member.isDead
              );
              const healthBadge = healthStateBadgeConfig[healthState];
              const isLeader = member.id === currentParty?.leaderCharacterId;
              // Only drop what the badge actually states. Filtering 'unconscious'
              // unconditionally hid a full-HP Sleep'd member entirely, because
              // that member resolves to UNINJURED.
              const displayConditions = visibleConditions(member.conditions, healthState);

              return (
                <div
                  key={member.id}
                  className={`p-3 border rounded-lg ${
                    member.isCurrentPlayer ? 'border-primary/50 bg-primary/5' : 'border-border'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Avatar
                      className={`h-14 w-14 ${member.portraitUrl ? 'cursor-pointer' : ''}`}
                      onClick={
                        member.portraitUrl
                          ? () =>
                              setMemberPortraitOverlay({
                                url: member.portraitUrl!,
                                name: member.name,
                              })
                          : undefined
                      }
                    >
                      {member.avatarUrl && <AvatarImage src={member.avatarUrl} alt={member.name} />}
                      <AvatarFallback className="text-lg">
                        {member.avatarEmoji || member.name[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1.5 mb-0.5">
                        <div className="flex items-center gap-1 min-w-0">
                          {isLeader && <LeaderCrown />}
                          <span className="font-medium text-sm truncate font-character">
                            {member.name}
                          </span>
                        </div>
                        {member.isCurrentPlayer ? (
                          <StatusPill className={`${status.bgColor} ${status.textColor}`}>
                            {status.label}
                          </StatusPill>
                        ) : (
                          <StatusPill className={healthBadge.className}>
                            {healthBadge.label}
                          </StatusPill>
                        )}
                      </div>
                      {/* Character class/species line */}
                      {(member.level || member.species || member.class) && (
                        <p className="text-xs text-muted-foreground truncate mb-1 uppercase tracking-wide">
                          {member.level && `Level ${member.level}`}
                          {member.level && (member.species || member.class) && ' '}
                          {member.species}
                          {member.species && member.class && ' '}
                          {member.class}
                        </p>
                      )}
                      {/* One wrapping row for every status pill, matching
                          PartyStatusPanel. Conditions and the resurrection penalty
                          used to stack as separate rows at different sizes; they
                          now flow left-to-right at a uniform `sm` and wrap only
                          when they run out of width. */}
                      <div className="mb-1 flex flex-wrap items-center gap-1 empty:mb-0">
                        {displayConditions.map((condition, idx) => (
                          <ConditionBadge
                            key={
                              typeof condition === 'string' ? condition : `${condition.name}-${idx}`
                            }
                            condition={condition}
                            size="sm"
                          />
                        ))}
                        {/* SRD 2024: -N to all d20 tests, -1 per long rest */}
                        {member.resurrectionPenalty !== undefined &&
                          member.resurrectionPenalty > 0 && (
                            <ResurrectionPenaltyBadge
                              penalty={member.resurrectionPenalty}
                              size="sm"
                            />
                          )}
                      </div>
                      {member.isCurrentPlayer && (
                        /* Player's own character — exact HP + bar */
                        <div className="space-y-1">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs">
                              HP: {member.hp}/{member.maxHp}
                            </span>
                          </div>
                          <HpBar current={member.hp} max={member.maxHp} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
        </CardContent>
      )}
      <ImageModal
        isOpen={!!memberPortraitOverlay}
        onClose={() => setMemberPortraitOverlay(null)}
        imageUrl={memberPortraitOverlay?.url}
        altText={memberPortraitOverlay?.name ?? ''}
      />
    </Card>
  );
}
